import axios, { AxiosResponse } from 'axios';
import { acquireSlot, releaseSlot } from './concurrency';

/**
 * Admin console API client.
 * Uses its own session keys (apnicrm_admin_*) so the CRM session and the
 * admin panel never overwrite each other's tokens.
 */
export const ADMIN_TOKEN_KEY = 'apnicrm_admin_token';
export const ADMIN_USER_KEY = 'apnicrm_admin_user';

// When the console is opened on the dedicated admin subdomain the API always
// lives on the main domain. On apnicrm.com/admin we keep it same-origin.
const resolveApiBase = (): string => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  try {
    const host = window.location.hostname.toLowerCase();
    if (host === 'admin.apnicrm.com' || host.startsWith('admin.')) {
      return 'https://apnicrm.com/api';
    }
  } catch {
    /* no window (SSR / tests) */
  }
  return '/api';
};

/**
 * Absolute URL of the store CRM app.
 * On the dedicated admin subdomain a relative "/crm/dashboard" would stay on
 * the admin host and just bounce back to Overview, so it must point at the
 * main domain there. On apnicrm.com (path-based admin) the relative path is
 * already correct.
 */
export const storeAppUrl = (path = '/crm/dashboard'): string => {
  try {
    const host = window.location.hostname.toLowerCase();
    if (host === 'admin.apnicrm.com' || host.startsWith('admin.')) {
      return `https://apnicrm.com${path}`;
    }
  } catch {
    /* no window (SSR / tests) */
  }
  return path;
};

const adminApi = axios.create({
  baseURL: resolveApiBase(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

adminApi.interceptors.request.use(async (config) => {
  await acquireSlot();
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (response: AxiosResponse) => { releaseSlot(); return response; },
  (error) => {
    releaseSlot();
    const isLoginCall = (error?.config?.url || '').includes('/auth/login');
    if (error.response?.status === 401 && !isLoginCall) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_USER_KEY);
      const base = process.env.PUBLIC_URL || '';
      const host = window.location.hostname.toLowerCase();
      const onAdminHost = host === 'admin.apnicrm.com' || host.startsWith('admin.');
      if (onAdminHost) {
        // Dedicated admin subdomain — admin lives at the site root.
        if (!window.location.pathname.endsWith('/login')) {
          window.location.href = '/login';
        }
      } else if (window.location.pathname.startsWith(`${base}/admin`) && !window.location.pathname.endsWith('/login')) {
        window.location.href = `${base}/admin/login`;
      }
    }
    return Promise.reject(error);
  }
);

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  staffName?: string | null;
  phoneNumber?: string;
  storeType?: string;
  isActive: boolean;
  createdAt?: string;
  subscription?: any;
}

export interface AdminPlan {
  id: string;
  name: string;
  price: number; // paise
  discountPercent: number;
  durationDays: number;
  maxCustomers: number;
  maxStaff: number;
  description: string;
  features: string[];
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  badge?: string;
}

export const adminSession = {
  getToken: () => localStorage.getItem(ADMIN_TOKEN_KEY),
  getUser: (): AdminUser | null => {
    try {
      const raw = localStorage.getItem(ADMIN_USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  save: (token: string, user: AdminUser) => {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
  },
  isLoggedIn: () => !!localStorage.getItem(ADMIN_TOKEN_KEY),
};

const canAdmin = (role?: string) => role === 'ADMIN' || role === 'SUPER_ADMIN';

export const adminService = {
  /** Login against the shared auth route, but only ADMIN/SUPER_ADMIN pass. */
  login: async (email: string, password: string) => {
    const { data } = await adminApi.post('/auth/login', { email, password });
    if (!canAdmin(data.user?.role)) {
      throw new Error('This account does not have admin access. Use the CRM login instead.');
    }
    adminSession.save(data.token, data.user);
    return data;
  },

  stats: () => adminApi.get('/admin/stats').then((r) => r.data),
  listUsers: () => adminApi.get('/admin/users').then((r) => r.data),
  createUser: (data: Record<string, any>) => adminApi.post('/admin/users', data).then((r) => r.data),
  updateUser: (id: string, data: Record<string, any>) =>
    adminApi.patch(`/admin/users/${id}`, data).then((r) => r.data),
  deleteUser: (id: string) => adminApi.delete(`/admin/users/${id}`).then((r) => r.data),

  listSubscriptions: () => adminApi.get('/admin/subscriptions').then((r) => r.data),
  updateSubscription: (id: string, data: Record<string, any>) =>
    adminApi.patch(`/admin/subscriptions/${id}`, data).then((r) => r.data),

  // Plan / package catalog (prices, discounts, limits, features)
  listPlans: () => adminApi.get('/admin/plans').then((r) => r.data),
  savePlans: (plans: any[]) => adminApi.put('/admin/plans', { plans }).then((r) => r.data),

  listPayments: () => adminApi.get('/admin/payments').then((r) => r.data),
  createPayment: (data: Record<string, any>) => adminApi.post('/admin/payments', data).then((r) => r.data),
  deletePayment: (userId: string, paymentId: string) =>
    adminApi.delete(`/admin/payments/${userId}/${paymentId}`).then((r) => r.data),

  listBackups: () => adminApi.get('/admin/backups').then((r) => r.data),
  createBackup: () => adminApi.post('/admin/backup').then((r) => r.data),
  resetData: () => adminApi.post('/admin/reset-data').then((r) => r.data),
};

export default adminApi;
